import classnames from "classnames";
import { Fragment, ReactNode } from "react";
import styles from "./typography.styles.module.scss";
import {
  TypographyBoldness,
  TypographyColor,
  TypographyElements,
  TypographyStyles,
} from "./typography.constants";

type TypographyProps = {
  boldness?: TypographyBoldness;
  children: ReactNode[] | ReactNode | number | string;
  color?: TypographyColor;
  element?: TypographyElements;
  isMasked?: boolean;
  rootClassName?: string;
  styling: TypographyStyles;
};

/**
 * @param isMasked will mask all text in amplitude analytics system - should be true for any names, emails, or other PII
 */
export const TypographyComponent = ({
  boldness = "regular",
  children,
  color,
  element,
  isMasked = false,
  rootClassName = "",
  styling,
}: TypographyProps) => {
  const styleClass = `${styling || "span"}-${boldness}`;
  const colorClass = `color-${color}`;
  const className = classnames(
    styles[styleClass as keyof typeof styles],
    styles[colorClass as keyof typeof styles],
    rootClassName
  );
  const attributes = { className, "data-amp-mask": isMasked ? "" : undefined };
  const display = element || styling;

  return (
    <Fragment>
      {["h1", "h1-xl"].includes(display) &&
        <h1 {...attributes}>{children}</h1>
      }
      {["h2", "h2-xl"].includes(display) &&
        <h2 {...attributes}>{children}</h2>
      }
      {["h3", "h3-xl"].includes(display) &&
        <h3 {...attributes}>{children}</h3>
      }
      {display === "h4" && <h4 {...attributes}>{children}</h4>}
      {display === "h5" && <h5 {...attributes}>{children}</h5>}
      {display === "h6" && <h6 {...attributes}>{children}</h6>}
      {[
        "p",
        "p-default",
        "p-xl",
        "p-lg",
        "p-sm",
        "p-xs",
        "p-xxs",
        "p-italic",
        "p-italic-sm",
        "disclaimer-sm",
        "disclaimer-default",
        "links-sm",
        "links-default",
        "list-sm",
        "list-default",
        "title-header-default",
        "title-header-lg",
        "title-header-sm",
        "title-header-xs",
        "title-header-xxs",
        "metadata-default",
        "metadata-sm",
        "metadata-lg",
        "metadata-xs",
      ].includes(display) && <p {...attributes}>{children}</p>}
      {display === "span" && <span {...attributes}>{children}</span>}
    </Fragment>
  );
};
